import { useState, useEffect } from "react"

import { CalendarSearch, FileSearch, FolderSearch, UserSearch, ScanSearch, TextSearch } from "lucide-react"

import { CalendarSection } from "@/components/filters/CalendarSection"
import { FilterSection } from "@/components/filters/FilterSection"
import { SearchPropertySection } from "@/components/filters/SearchPropertySection"
import { SearchSection } from "@/components/filters/SearchSection"
import { Button } from "@ui/button"
import { Label } from "@ui/label"
import { Switch } from "@ui/switch"
import { AUTO_SEARCH_STATE } from "@/constants/cookies"
import { SEARCHABLE_PROPERTIES } from "@/constants/searchableProperties"
import { transformUser } from "@/lib/facets"
import { FacetResponse, PropertyFacet } from "@/models/facets"

interface FiltersPanelProps {
    properties?: PropertyFacet[]
    statuses: FacetResponse[]
    types: FacetResponse[]
    users: FacetResponse[]
    onFiltersChange: (filters: any) => void
    onSearch: () => void
    onClearFilters: number
}
export function FiltersPanel({ properties, statuses, types, users, onFiltersChange, onSearch, onClearFilters }: Readonly<FiltersPanelProps>) {
    const [filters, setFilters] = useState({})
    const handleDateChange = (date: any) => {
        if (date === undefined) {
            delete (filters as any).date
            setFilters({ ...filters })
        } else {
            setFilters({ ...filters, date })
        }
    }
    const handlePropertiesChange = (properties: any) => {
        if (properties === undefined) {
            delete (filters as any).properties
            setFilters({ ...filters })
        } else {
            setFilters({ ...filters, properties })
        }
    }
    const handleTypeChange = (type: string) => {
        if (type === undefined) {
            delete (filters as any).type
            setFilters({ ...filters })
        } else {
            setFilters({ ...filters, type })
        }
    }
    const handleStatusChange = (status: string) => {
        if (status === undefined) {
            delete (filters as any).status
            setFilters({ ...filters })
        } else {
            setFilters({ ...filters, status })
        }
    }
    const handleUserChange = (user: string) => {
        if (user === undefined) {
            delete (filters as any).user
            setFilters({ ...filters })
        } else {
            setFilters({ ...filters, user })
        }
    }
    const handleVectorTextChange = (vectorText: string) => {
        if (vectorText === undefined) {
            delete (filters as any).vectorText
            setFilters({ ...filters })
        } else {
            setFilters({ ...filters, vectorText })
        }
    }

    const [triggerSearch, setTriggerSearch] = useState(0)

    const [autoSearch, setAutoSearch] = useState(localStorage.getItem(AUTO_SEARCH_STATE) === "auto")
    const handleAutoSearchChange = (checked: boolean) => {
        localStorage.setItem(AUTO_SEARCH_STATE, checked ? "auto" : "manual")
        setAutoSearch(checked)

        if (checked) {
            setTriggerSearch(triggerSearch + 1)
        }
    }

    useEffect(() => {
        onFiltersChange(filters)

        if (!autoSearch) {
            return
        }

        setTriggerSearch(triggerSearch + 1)
    }, [filters])

    useEffect(() => {
        onSearch()
    }, [triggerSearch])

    useEffect(() => {
        setFilters({})
    }, [onClearFilters])

    return (
        <div className="h-full flex w-[var(--filterbar-width)] flex-col bg-sidebar text-sidebar-foreground border-r border-sidebar-border">
            <div className="h-full flex flex-col overflow-auto">
                <SearchSection label="Semantic Search" placeholder="Type what you are looking for" Icon={ScanSearch} onClear={onClearFilters} onSelection={handleVectorTextChange} />
                <SearchPropertySection label="Property Search" Icon={TextSearch} simpleProperties={SEARCHABLE_PROPERTIES} advancedProperties={properties} onClear={onClearFilters} onSelection={handlePropertiesChange} />
                <FilterSection section="Types" placeholder="Filter Types" items={types} Icon={FolderSearch} onClear={onClearFilters} onSelection={handleTypeChange} />
                <FilterSection section="Statuses" placeholder="Filter Statuses" items={statuses} Icon={FileSearch} onClear={onClearFilters} onSelection={handleStatusChange} />
                <CalendarSection section="Calendar" Icon={CalendarSearch} onClear={onClearFilters} onSelection={handleDateChange} />
                <FilterSection section="Users" placeholder="Filter Users" items={users} Icon={UserSearch} onClear={onClearFilters} onSelection={handleUserChange} transform={transformUser} />
            </div>
            <div className="w-full p-2 text-center ">
                <div className="flex items-center space-x-2">
                    <Switch id="search-mode" checked={autoSearch} onCheckedChange={handleAutoSearchChange} />
                    <Label htmlFor="search-mode" className="text-xs">Auto Search</Label>
                </div>
                <Button onClick={onSearch} disabled={autoSearch} className="mt-2 w-full hover:cursor-pointer">Search</Button>
            </div>
        </div>
    )
}